<template>
  <div class="pfe-root">

    <v-toolbar color="white" elevation="1" dense class="flex-shrink-0 pfe-toolbar">
      <v-btn icon small @click="$emit('on-close')" class="ml-2">
        <v-icon small>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title class="text-caption font-weight-medium blue-grey--text text--darken-2">
        {{ $t('Site.PdfManipulation.EditPdfs') }}
      </v-toolbar-title>
      <v-spacer />
      <v-tooltip bottom z-index="1000">
        <template v-slot:activator="{ on }">
          <v-btn
            icon small
            :disabled="!hasMergeTargets"
            v-on="on"
            @click="openMergeDialogFromSelected"
          >
            <v-icon small :color="hasMergeTargets ? 'primary' : 'grey lighten-1'">mdi-merge</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('MergeDocuments') }}</span>
      </v-tooltip>
      <v-spacer />
      <v-chip v-if="actionQueue.length > 0" x-small outlined color="orange darken-1" class="mr-3">
        {{ actionQueue.length }} {{ $t('Site.PdfManipulation.PendingOperations') }}
      </v-chip>
      <v-btn
        small color="primary" depressed
        :disabled="actionQueue.length === 0"
        :loading="saving"
        @click="saveChanges"
      >
        <v-icon x-small class="mr-1">mdi-content-save</v-icon>
        {{ $t('Save') }}
      </v-btn>
    </v-toolbar>

    <div class="d-flex flex-grow-1 overflow-hidden">
      <splitpanes class="default-theme pfe-splitpanes" @resized="onPaneResized">
        <pane :size="panelSize" min-size="12" max-size="45">
          <pdf-document-panel
            :documents="documents"
            :selected-page="selectedPage"
            :doc-colors="docColors"
            :loading="loading"
            @select-page="selectPage"
            @navigate="navigatePage"
            @context-menu="openContextMenu"
          />
        </pane>
        <pane :size="100 - panelSize" min-size="55">
          <pdf-page-viewer
            ref="viewer"
            :documents="documents"
            :selected-page="selectedPage"
            :doc-color-hex="docColorHex"
            @select-page="selectPage"
          />
        </pane>
      </splitpanes>
    </div>

    <v-menu
      v-model="ctxMenu.visible"
      :position-x="ctxMenu.x"
      :position-y="ctxMenu.y"
      absolute offset-y z-index="1000"
    >
      <v-list dense>
        <v-list-item @click="openMergeDialog">
          <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-merge</v-icon></v-list-item-icon>
          <v-list-item-title class="primary--text">{{ $t('MergeDocuments') }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <pdf-merge-dialog
      v-model="mergeDialog"
      :source-doc="ctxMenu.doc"
      :source-page="ctxMenu.page"
      :target-docs="mergeTargets"
      :can-delete="canDeleteSourceDoc"
      @merge="onMerge"
    />

  </div>
</template>

<script>
import DocumentService from '@/services/DocumentService';
import Document from '@/ecm/models/document/Document';
import PdfDocumentPanel from './PdfDocumentPanel.vue';
import PdfPageViewer from './PdfPageViewer.vue';
import PdfMergeDialog from './PdfMergeDialog.vue';
import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';

const DOC_COLORS = ['cyan darken-2', 'deep-purple', 'green darken-1', 'orange darken-1', 'pink darken-1'];
const DOC_COLOR_HEX = ['#00838f', '#512da8', '#388e3c', '#e65100', '#c2185b'];

let _pdfjsLib = null;
async function getPdfJs() {
    if (!_pdfjsLib) {
        _pdfjsLib = await import(/* webpackIgnore: true */ '/js/pdf/build/pdf.mjs');
        _pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf/build/pdf.worker.mjs';
    }
    return _pdfjsLib;
}

export default {
    name: 'PdfEditorView',
    components: { PdfDocumentPanel, PdfPageViewer, PdfMergeDialog, Splitpanes, Pane },
    data() {
        return {
            documents: [],
            selectedPage: null,
            actionQueue: [],
            saving: false,
            loading: false,
            docColors: DOC_COLORS,
            docColorHex: DOC_COLOR_HEX,
            ctxMenu: { visible: false, x: 0, y: 0, doc: null, page: null },
            mergeDialog: false,
            panelSize: 22,
        };
    },
    props: {
        documentIds: { type: Array, default: () => [] },
        permissions: { type: Object, default: () => ({}) },
    },
    computed: {
        mergeTargets() {
            if (!this.ctxMenu.doc) return [];
            return this.documents.filter(d => d.id !== this.ctxMenu.doc.id && !d.merged);
        },
        hasMergeTargets() {
            if (!this.selectedPage) return false;
            return this.documents.some(d => d.id !== this.selectedPage.docId && !d.merged);
        },
        canDeleteSourceDoc() {
            if (!this.ctxMenu.doc) return false;
            const entry = this.permissions[(this.ctxMenu.doc.id)];
            return !!(entry && entry.canDelete);
        },
    },
    created() {
        this.loadDocuments();
    },
    methods: {
        async loadDocuments() {
            this.loading = true;
            this.documents = this.documentIds.map(id => (
                { id: id, name: id, expanded: false, pages: [], merged: false, pdfDoc: null, dto: null, docModel: null }
            ));

            const pdfjsLib = await getPdfJs();

            const fullObjectDataResponse = await Promise.all(
                this.documentIds.map(id =>
                    DocumentService.get(id)
                        .then(dto => {
                            const initialDoc = new Document(dto);
                            return DocumentService.getStructureWithData(dto.ContentTypeID, initialDoc.toDto([]).Fields)
                                .then(structDto => {
                                    const docModel = new Document(structDto);
                                    docModel.id = parseInt(id);
                                    return { id: id, name: dto.AttachmentFileName || dto.ObjectID, dto: dto, docModel: docModel };
                                });
                        })
                        .catch(() => null)
                )
            );
            fullObjectDataResponse.forEach(n => {
                if (!n) return;
                const doc = this.documents.find(d => d.id === n.id);
                if (doc) { doc.name = n.name; doc.dto = n.dto; doc.docModel = n.docModel; }
            });

            for (let i = 0; i < this.documents.length; i++) {
                const doc = this.documents[i];
                try {
                    const blob = await DocumentService.getPdfBlob(doc.id);
                    const arrayBuffer = await blob.arrayBuffer();
                    const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
                    doc.pdfDoc = pdfDoc;
                    doc.pages = Array.from({ length: pdfDoc.numPages }, (_, k) => (
                        { index: k + 1, sourceDocId: doc.id, pdfDoc: pdfDoc, pdfPageNum: k + 1 }
                    ));
                    await this.$nextTick();
                    if (this.$refs.viewer) this.$refs.viewer.renderThumbnailsForDoc(doc);
                } catch (e) {
                    doc.pages = [];
                }
            }

            this.loading = false;
            const firstDoc = this.documents.find(d => d.pages.length > 0);
            if (firstDoc) this.selectPage(firstDoc, firstDoc.pages[0]);
        },

        selectPage(doc, page) {
            this.selectedPage = { docId: doc.id, docName: doc.name, page: page };
        },

        navigatePage(delta) {
            if (!this.selectedPage) return;
            const all = this.getAllPages();
            const current = this.selectedPage;
            const idx = all.findIndex(p => p.docId === current.docId && p.page === current.page);
            var next;
            if (delta === -Infinity) next = all[0];
            else if (delta === Infinity) next = all[all.length - 1];
            else next = all[idx + delta];
            if (next) {
                const doc = this.documents.find(d => d.id === next.docId);
                if (doc) this.selectPage(doc, next.page);
            }
        },

        getAllPages() {
            return this.documents.reduce((acc, doc) =>
                acc.concat(doc.pages.map(page => ({ docId: doc.id, page: page }))), []);
        },

        openContextMenu(event, doc, page) {
            this.ctxMenu = { visible: true, x: event.clientX, y: event.clientY, doc: doc, page: page };
        },

        openMergeDialog() {
            this.ctxMenu.visible = false;
            this.mergeDialog = true;
        },

        openMergeDialogFromSelected() {
            if (!this.selectedPage) return;
            const doc = this.documents.find(d => d.id === this.selectedPage.docId);
            if (!doc) return;
            this.ctxMenu.doc = doc;
            this.ctxMenu.page = this.selectedPage.page;
            this.mergeDialog = true;
        },

        onMerge(payload) {
            const sourceDoc = this.ctxMenu.doc;
            const targetDoc = this.documents.find(d => d.id === payload.targetDocId);
            if (!sourceDoc || !targetDoc) return;

            const pagesRef = payload.pageIndices
                ? sourceDoc.pages.filter(p => payload.pageIndices.indexOf(p.index) !== -1)
                : sourceDoc.pages.slice();

            const pdfPageNums = pagesRef.map(p => p.pdfPageNum);
            const removePagesFromSource = !payload.keepOriginal && pdfPageNums.length < sourceDoc.pages.length;

            this.actionQueue.push({
                type: 'merge',
                sourceDocumentId: sourceDoc.id,
                targetDocumentId: targetDoc.id,
                pages: pdfPageNums,
                removePagesFromSource: removePagesFromSource,
                label: sourceDoc.name + ' \u2192 ' + targetDoc.name + ' (' + pdfPageNums.length + ' pgs)',
            });

            this.applyMergeVisual(sourceDoc, targetDoc, pdfPageNums, payload.keepOriginal);
            this.mergeDialog = false;
        },
        applyMergeVisual(sourceDoc, targetDoc, pageIndices, keepOriginal) {

            const indices = pageIndices ? new Set(pageIndices) : null;

            const pagesRef = indices
                ? sourceDoc.pages.filter(p => indices.has(p.index))
                : sourceDoc.pages.slice();

            if (keepOriginal) {
                const copies = pagesRef.map(p => ({
                    index: 0,
                    sourceDocId: p.sourceDocId,
                    pdfDoc: p.pdfDoc,
                    pdfPageNum: p.pdfPageNum
                }));

                targetDoc.pages = targetDoc.pages.concat(copies);
                targetDoc.pages.forEach((p, i) => {
                    p.index = i + 1;
                });
            } else {

                const remaining = indices
                    ? sourceDoc.pages.filter(p => !indices.has(p.index))
                    : [];

                targetDoc.pages = targetDoc.pages.concat(pagesRef);
                targetDoc.pages.forEach((p, i) => {
                    p.index = i + 1;
                });

                sourceDoc.pages = remaining;
                targetDoc.pages.forEach((p, i) => {
                    p.index = i + 1;
                });

                if (!sourceDoc.pages.length) {
                    sourceDoc.merged = true;
                }

                if (this.selectedPage && this.selectedPage.docId === sourceDoc.id) {
                    const currentPage = this.selectedPage.page;

                    if (!sourceDoc.pages.includes(currentPage)) {

                        if (targetDoc.pages.includes(currentPage)) {
                            this.selectedPage = {
                                docId: targetDoc.id,
                                docName: targetDoc.name,
                                page: currentPage
                            };
                        } else {
                            this.selectedPage = null;
                        }
                    }
                }
            }

            this.$nextTick(() => {
                if (!this.$refs.viewer) return;

                this.$refs.viewer.renderThumbnailsForDoc(targetDoc);

                if (!keepOriginal && sourceDoc.pages.length > 0) {
                    this.$refs.viewer.renderThumbnailsForDoc(sourceDoc);
                }
            });
        },

        onPaneResized(panes) {
            this.panelSize = panes[0].size;
        },

        async saveChanges() {
            if (!this.actionQueue.length) return;
            this.saving = true;
            try {
                const involvedDocIds = new Set(
                    this.actionQueue.flatMap(op => [String(op.sourceDocumentId), String(op.targetDocumentId)])
                );
                const manipulatedContentTypeIds = [...new Set(
                    this.documents
                        .filter(d => involvedDocIds.has(String(d.id)) && d.dto)
                        .map(d => d.dto.ContentTypeID)
                )];

                const results = await DocumentService.applyPdfOperations({
                    manipulatedContentTypeIds: manipulatedContentTypeIds,
                    operations: this.actionQueue.map(op => ({
                        type: op.type,
                        sourceDocumentId: parseInt(op.sourceDocumentId),
                        targetDocumentId: parseInt(op.targetDocumentId),
                        pages: op.pages,
                        removePagesFromSource: op.removePagesFromSource || false,
                    })),
                });
                await Promise.all(
                    results.map(result => {
                        const doc = this.documents.find(d => String(d.id) === String(result.objectId));
                        if (!doc || !doc.docModel) {
                            console.warn('[PdfEditor] no doc found for objectId:', result.objectId);
                            return Promise.resolve();
                        }
                        const payload = doc.docModel.toDto([{ id: -1, fileName: doc.dto.AttachmentFileName, fileId: result.tempFileId }]);
                        return DocumentService.save(payload);
                    })
                );

                const emptyDocIds = this.documents
                    .filter(d => d.merged)
                    .map(d => parseInt(d.id));

                if (emptyDocIds.length) {
                    await DocumentService.remove(emptyDocIds);
                }

                this.$emit('on-close');
            } catch (e) {
                console.error('[PdfEditor] saveChanges error:', e);
                this.saving = false;
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.pfe-root {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  flex-direction: column;
}
.pfe-toolbar {
  position: relative;
  z-index: 2;
}
</style>

<style>
.pfe-splitpanes.splitpanes.default-theme {
  height: 100%;
}
.pfe-splitpanes.splitpanes.default-theme .splitpanes__pane {
  overflow: hidden;
}
</style>
